import React, { Component } from 'react';
import { connect } from 'react-redux';
import { findIndex } from 'lodash';
import Dotdotdot from 'react-dotdotdot';
import PropTypes from 'prop-types';
import { getEventDateStringV2 } from '../../../utils/formatting';
import Description from '../../text/Description';
import { LinkAnchor } from '../../Links';
import AddEventToCalendar from '../../AddToCalander';
import { getEventRedirectUrl, getUseAnchorForEventLink, isPrivateEvent, getPaidHitterExperience, EventTypes, isPickleBallSportTypeEvent, isEventType, isImportedProfileEvent, isLeagueSessionProfileEvent, buildUnifiedEventCardModel } from '../../../utils/events';
import { Utr } from '../../text';
import SportLabel from '../CardComponents/SportLabel';
import { ratingTypes } from '../../../utils/constants';
import USAPLogo from '../../USAPLogo';
import { copyImportedEventShareLink } from '../../ImportedEvents/importedEventShare';
import { trackImportedEventShared } from '../../ImportedEvents/importedEventTracking';
import { ShareSurfaceByEntryPoint } from '../../Analytics/constants';
import { TmsEventUsapApplicationStatusTypes, sportTypeIds } from '../../../utils/eventConstants';
import Img from '../../Img';

const _ = { findIndex };

const importedFormatIcons = {
    Singles: '/img/v3/icons/icon-singles-black.svg',
    Doubles: '/img/v3/icons/icon-doubles-black.svg',
};

// Do not switch these to the `-black` pair despite its name: those two disagree in viewBox
// (14x14 vs 23x18) and are drawn in #5A7290, so a shared icon box renders them at different sizes.
const unifiedFormatIcons = {
    Singles: '/img/v3/icons/icon-singles-new.svg',
    Doubles: '/img/v3/icons/icon-doubles-new.svg',
};

const unifiedShareIcon = '/img/v3/icons/icon-24px-share-new.svg';

/*
    Props:
    tourTag - Manually override the displayed tour on the result
*/

class EventCard extends Component {
    constructor(props) {
        super(props);
        this.trimStringToWords = this.trimStringToWords.bind(this);
        this.formatEventName = this.formatEventName.bind(this);
        this.handleShareClick = this.handleShareClick.bind(this);
        this.handleOrganizerClick = this.handleOrganizerClick.bind(this);
        this.handleOrganizerKeyDown = this.handleOrganizerKeyDown.bind(this);
    }

    async handleShareClick(clickEvent) {
        clickEvent.preventDefault();
        clickEvent.stopPropagation();
        const { event, dispatch, entryPoint } = this.props;
        const path = getEventRedirectUrl(event?.id, event?.source);
        if (!path || !dispatch) {
            return;
        }
        const shareUrl = path.startsWith('http') ? path : `${window.location.origin}${path}`;
        const copied = await copyImportedEventShareLink(dispatch, shareUrl);

        // Only imported events are instrumented, and only once the copy succeeded. This control
        // also renders for native events on the Events tab, which stay untracked. The owning tab
        // supplies its identity as entryPoint, which maps to the matching share surface.
        if (copied && isImportedProfileEvent(event)) {
            trackImportedEventShared({
                eventId: event?.id,
                shareSurface: ShareSurfaceByEntryPoint[entryPoint] ?? null,
            });
        }
    }

    // The whole card is already a link to the event, so the organizer cannot be an anchor of its own
    // without nesting one inside another. It navigates from a click instead, and stops the event
    // link from also firing.
    handleOrganizerClick(clickEvent, clubId) {
        clickEvent.preventDefault();
        clickEvent.stopPropagation();
        const { history } = this.props;
        const path = `/clubs/${clubId}`;
        if (history) {
            history.push(path);
            return;
        }
        window.location.assign(path);
    }

    handleOrganizerKeyDown(keyEvent, clubId) {
        if (keyEvent.key !== 'Enter' && keyEvent.key !== ' ') {
            return;
        }
        this.handleOrganizerClick(keyEvent, clubId);
    }

    trimStringToWords(str, numWords) {
        const words = str.split('');
        if (words.length > numWords) {
            return `${words.slice(0, numWords).join('')}...`;
        }
        return str;
    }

    formatEventName(str) {
        if (this.props.isOnDashboard || this.props.isProfilePage) {
            const string = str.replace(/_/g, ' ');
            return this.trimStringToWords(string, 40);
        }

        return str.replace(/_/g, ' ');
    }

    renderImportedCard({ eventDateString, verifiedClassName }) {
        const { event, 'data-testid': dataTestId } = this.props;
        const {
            id,
            name,
            source,
            sportTypeId,
            thirdPartyIdentifier,
            importedEventType,
            importedEventVerified,
            importedFormats = [],
            importedGender,
            registeredCount,
        } = event;
        const importedTestId = dataTestId && `${dataTestId}.imported`;
        const hasFormats = importedFormats.length > 0;

        return (
            <div>
                <LinkAnchor
                    link={getEventRedirectUrl(id, source)}
                    useAnchor={getUseAnchorForEventLink(source)}
                    target={'_self'}
                    data-testid={dataTestId && `${dataTestId}.card-link`}
                >
                    <div className="utr-card event-card">
                        <div className="imported-view" data-testid={importedTestId && `${importedTestId}.view`}>
                            <div className="date-row" data-testid={importedTestId && `${importedTestId}.date-row`}>
                                <span className="date" data-testid={importedTestId && `${importedTestId}.date`}>{eventDateString}</span>
                                <div className="labels">
                                    <span className="role-label" data-testid={importedTestId && `${importedTestId}.role-label`}>PLAYER</span>
                                    <SportLabel sportTypeId={sportTypeId} data-testid={dataTestId && `${dataTestId}.sport-label`} />
                                </div>
                            </div>
                            <div className="meta-row" data-testid={importedTestId && `${importedTestId}.meta-row`}>
                                <span className="event-type" data-testid={importedTestId && `${importedTestId}.event-type`}>{importedEventType?.toUpperCase()}</span>
                                {thirdPartyIdentifier && (
                                    <span data-testid={importedTestId && `${importedTestId}.identifier`}>{thirdPartyIdentifier}</span>
                                )}
                                {importedEventVerified && (
                                    <div
                                        className={`utr-icon ${verifiedClassName}`}
                                        title="Verified"
                                        data-testid={importedTestId && `${importedTestId}.verified-badge`}
                                    />
                                )}
                            </div>
                            <div className="name" title={name} data-testid={importedTestId && `${importedTestId}.name`}>
                                <Dotdotdot clamp={2}>
                                    {(name || '').replace(/_/g, ' ')}
                                </Dotdotdot>
                            </div>
                            {(hasFormats || importedGender) && (
                                <div className="detail-row" data-testid={importedTestId && `${importedTestId}.detail-row`}>
                                    {hasFormats && (
                                        <span className="formats" data-testid={importedTestId && `${importedTestId}.formats`}>
                                            {importedFormats.map(format => (
                                                <span
                                                    className="format"
                                                    key={format}
                                                    data-testid={importedTestId && `${importedTestId}.format-${format.toLowerCase()}`}
                                                >
                                                    {importedFormatIcons[format] && <Img src={importedFormatIcons[format]} alt={format} />}
                                                    {format}
                                                </span>
                                            ))}
                                        </span>
                                    )}
                                    {importedGender && (
                                        <span className="gender" data-testid={importedTestId && `${importedTestId}.gender`}>{importedGender}</span>
                                    )}
                                </div>
                            )}
                            {registeredCount > 0 && (
                                <div className="player-count" data-testid={importedTestId && `${importedTestId}.player-count`}>
                                    {`${registeredCount} Players`}
                                </div>
                            )}
                        </div>
                    </div>
                </LinkAnchor>
            </div>
        );
    }

    renderUnifiedCard({ model, verifiedClassName, preserved }) {
        const { event, showShareAction, compactProfileCard, entryPoint, 'data-testid': dataTestId } = this.props;
        const unifiedTestId = dataTestId && `${dataTestId}.unified`;
        const { dateText, roleLabels, sportTypeId, eyebrow, identifier, verified, title, subtitleParts, detailLeading, detailTrailing, formats, playerCount } = model;
        const renderDetailPart = (part, key) => (
            <span className="detail-part" key={key} data-testid={unifiedTestId && `${unifiedTestId}.detail-part-${part}`}>
                {part}
            </span>
        );
        const formatsNode = formats.length > 0 ? (
            <span className="formats detail-part" key="formats" data-testid={unifiedTestId && `${unifiedTestId}.formats`}>
                {formats.map(format => (
                    <span
                        className="format"
                        key={format}
                        data-testid={unifiedTestId && `${unifiedTestId}.format-${format.toLowerCase()}`}
                    >
                        {!compactProfileCard && unifiedFormatIcons[format] && <Img src={unifiedFormatIcons[format]} alt={format} />}
                        {format}
                    </span>
                ))}
            </span>
        ) : null;
        const leadingNodes = detailLeading.map((part, index) => renderDetailPart(part, `leading-${index}`));
        const trailingNodes = detailTrailing.map((part, index) => renderDetailPart(part, `trailing-${index}`));
        // Every row on the Overview card truncates, so each carries its own full text as a hover.
        const rowTitle = parts => (compactProfileCard ? parts.filter(Boolean).join(' • ') : null);
        const detailGroups = [
            { key: 'fees', nodes: leadingNodes, title: rowTitle(detailLeading) },
            {
                key: 'attributes',
                nodes: [formatsNode, ...trailingNodes].filter(Boolean),
                title: rowTitle([formats.join(' / '), ...detailTrailing]),
            },
        ];
        const detailRows = detailGroups.filter(group => group.nodes.length > 0);
        // Tells the Imported Event Page which surface the click came from, for the
        // imported_event_page_viewed entryPoint property (TMS-2221). Router state rather than a
        // query param, so it never reaches the copyable share URL — a page opened from a shared
        // link is meant to report a null entry point. Undefined for every other card source and
        // for any consumer that does not opt in by passing entryPoint.
        const importedEntryState = entryPoint && isImportedProfileEvent(event) ? { entryPoint } : undefined;

        return (
            <div className={`unified-card-wrapper ${compactProfileCard ? 'unified-card-wrapper-compact' : ''}`}>
                <LinkAnchor
                    link={getEventRedirectUrl(event.id, event.source)}
                    useAnchor={getUseAnchorForEventLink(event.source)}
                    target={'_self'}
                    state={importedEntryState}
                    data-testid={dataTestId && `${dataTestId}.card-link`}
                >
                    <div className="utr-card event-card">
                        <div
                            className={`unified-view ${compactProfileCard ? 'unified-view-compact' : ''}`}
                            data-testid={unifiedTestId && `${unifiedTestId}.view`}
                        >
                            <div className="date-row" data-testid={unifiedTestId && `${unifiedTestId}.date-row`}>
                                <span className="date" title={dateText} data-testid={unifiedTestId && `${unifiedTestId}.date`}>{dateText}</span>
                                <div className="labels">
                                    {roleLabels.map(role => (
                                        <span
                                            className="role-label"
                                            key={role}
                                            data-testid={unifiedTestId && `${unifiedTestId}.role-label-${role.toLowerCase()}`}
                                        >
                                            {role.toUpperCase()}
                                        </span>
                                    ))}
                                    <SportLabel sportTypeId={sportTypeId} data-testid={dataTestId && `${dataTestId}.sport-label`} />
                                </div>
                            </div>
                            <div className="meta-row" data-testid={unifiedTestId && `${unifiedTestId}.meta-row`}>
                                {eyebrow && (
                                    <span className="event-type" data-testid={unifiedTestId && `${unifiedTestId}.event-type`}>{eyebrow}</span>
                                )}
                                {identifier && (
                                    <span data-testid={unifiedTestId && `${unifiedTestId}.identifier`}>{identifier}</span>
                                )}
                                {verified && (
                                    <div
                                        className={`utr-icon ${verifiedClassName}`}
                                        title="Verified"
                                        data-testid={unifiedTestId && `${unifiedTestId}.verified-badge`}
                                    />
                                )}
                                {preserved.recurringTag}
                                {preserved.statusTag}
                            </div>
                            <div className="name" title={title} data-testid={unifiedTestId && `${unifiedTestId}.name`}>
                                <Dotdotdot clamp={2}>
                                    {title}
                                </Dotdotdot>
                            </div>
                            {subtitleParts.length > 0 && (
                                <div className="subtitle-row" data-testid={unifiedTestId && `${unifiedTestId}.subtitle-row`}>
                                    {subtitleParts.map((part, partIndex) => {
                                        const partTestId = unifiedTestId && `${unifiedTestId}.subtitle-part-${partIndex}`;
                                        if (!part.clubId) {
                                            return (
                                                <span className="subtitle-part" key={part.text} data-testid={partTestId}>
                                                    {part.text}
                                                </span>
                                            );
                                        }
                                        return (
                                            <span
                                                className="subtitle-part subtitle-part-organizer"
                                                key={part.text}
                                                role="link"
                                                tabIndex={0}
                                                onClick={clickEvent => this.handleOrganizerClick(clickEvent, part.clubId)}
                                                onKeyDown={keyEvent => this.handleOrganizerKeyDown(keyEvent, part.clubId)}
                                                data-testid={partTestId}
                                            >
                                                {part.text}
                                            </span>
                                        );
                                    })}
                                </div>
                            )}
                            {detailRows.length > 0 && (
                                <div className="detail-rows" data-testid={unifiedTestId && `${unifiedTestId}.detail-rows`}>
                                    {detailRows.map(group => (
                                        <div
                                            className={`detail-row detail-row-${group.key}`}
                                            key={group.key}
                                            title={group.title}
                                            data-testid={unifiedTestId && `${unifiedTestId}.detail-row-${group.key}`}
                                        >
                                            {group.nodes}
                                        </div>
                                    ))}
                                </div>
                            )}
                            {preserved.paidHitterExperience}
                            {/* The row has a min-height for the share button, so rendering it empty
                                reserves a line the card has nothing to put in. */}
                            {(playerCount > 0 || showShareAction) && (
                            <div className="footer-row" data-testid={unifiedTestId && `${unifiedTestId}.footer-row`}>
                                {playerCount > 0 && (
                                    <div className="player-count" data-testid={unifiedTestId && `${unifiedTestId}.player-count`}>
                                        {`${playerCount} Players`}
                                    </div>
                                )}
                                {showShareAction && (
                                    <button
                                        type="button"
                                        className="share-action"
                                        aria-label="Copy link to this event"
                                        onClick={this.handleShareClick}
                                        data-testid={unifiedTestId && `${unifiedTestId}.share-btn`}
                                    >
                                        <Img src={unifiedShareIcon} alt="" />
                                    </button>
                                )}
                            </div>
                            )}
                            {preserved.completeRegistration}
                            {preserved.usapLogo}
                        </div>
                    </div>
                </LinkAnchor>
            </div>
        );
    }

    render() {
        const { force = {}, forceCardView, auth, tourTag, event, isAdminOrOwner, selectButton, onSelectButtonClick, showInvitation, onAcceptClick, onDeclineClick, invitingMember, showRegister, coreUI, isOnDashboard = false, showCompleteRegistration = false, unifiedProfileCard = false, compactProfileCard = false, 'data-testid': dataTestId } = this.props;
        const {
            id,
            name,
            isDemoEvent,
            published,
            eventDirectors,
            eventState,
            eventLocations,
            utrType,
            currentMember,
            club,
            source,
            eventType,
            eventTours,
            teamType,
            utrRange,
            priceRange,
            priceRangeShort,
            gender,
            hasPrizeMoney,
            prizeMoneyText,
            eventSchedule,
            eventSettings,
            paidHits,
            sportTypeId,
            registeredCount,
            isRecurringEvent,
        } = this.props.event;
        const playerExperienceTypesObject = coreUI?.types.playerExperienceTypes;
        const coachExperienceTypesObject = coreUI?.types.coachingExperienceTypes;
        const isFreePlayEvent = isEventType(event, EventTypes.FREE_PLAY);
        const isPaidHitEvent = isEventType(event, EventTypes.PAID_HIT);
        const shouldHideClubName = isFreePlayEvent || isPaidHitEvent;
        const isPickleBallEvent = isPickleBallSportTypeEvent(sportTypeId);
        const gameTypes = coreUI?.types?.tms?.gameTypes;
        const usapVerificationTypeId = event?.usapVerificationTypeId;
        const usapApplicationStatusId = event?.usapApplicationStatusId;
        const locationCityStateZip = (eventLocations && eventLocations.length > 0) ? eventLocations[0].display || eventLocations[0].placeName : null;
        const cardHeight = 205;
        const verifiedClassName = sportTypeId === sportTypeIds.PICKLEBALL ? 'icon-verified-green' : 'icon-verified-blue';

        const getGameType = () => {
            if (eventType?.id !== EventTypes.FREE_PLAY) {
                return teamType;
            }
            return gameTypes?.find(x => x.id === eventSettings?.gameTypeId)?.label;
        };

        const userIsPowered = auth?.user?.isPowered;
        const isPlaying = currentMember && (currentMember.playing || currentMember.isPlaying);
        const isOrganizer = (auth?.user?.id && eventDirectors &&
            (_.findIndex(eventDirectors, { memberId: auth.user.id }) !== -1)) ||
            currentMember?.eventDirectorRole || isAdminOrOwner;

        /* Description items desktop */
        const descriptionLine1 = [locationCityStateZip];
        if (hasPrizeMoney && prizeMoneyText && !isPaidHitEvent) {
            descriptionLine1.push(prizeMoneyText);
        }

        if ((isOrganizer || event?.playerCountVisibility) && registeredCount > 0) {
            descriptionLine1.push(`${registeredCount} Players`);
        }

        const descriptionLine2 = [];
        if (priceRange) {
            descriptionLine2.push(priceRange);
        }
        if (utrRange) {
            if (isPickleBallEvent) {
                descriptionLine2.push(`${ratingTypes.pickleball} ${utrRange}`);
            } else {
                descriptionLine2.push(`${ratingTypes.tennis} ${utrRange}`);
            }
        }
        if (gender && !isPaidHitEvent) {
            descriptionLine2.push(gender);
        }
        if ((teamType || eventSettings?.gameTypeId) && !isPaidHitEvent) {
            const gameType = getGameType();
            descriptionLine2.push(gameType);
        }

        const isImportedEvent = source === 'scraped-import';

        const isClubMember = (auth?.user?.id && club &&
            (_.findIndex(auth.user.clubMemberships, { clubId: club.id }) !== -1));

        const showMemberEventTag = isPrivateEvent(this.props.event) && !isClubMember;

        const colorStateV2 = () => {
            switch (eventState?.value) {
                case 'pre_registration':
                case 'completed':
                    return 'gray';
                case 'in_progress':
                    return '';
                default:
                    return '';
            }
        };

        const getStatusTag = (block, tagPrefix) => {
            const blockClass = block ? 'tag-block' : '';
            const statusTestId = tagPrefix && `${tagPrefix}.status-tag`;
            const shortTestId = tagPrefix && `${tagPrefix}.status-tag.short`;
            const longTestId = tagPrefix && `${tagPrefix}.status-tag.long`;
            const closedLabel = isImportedEvent ? 'Scheduled' : 'Reg Closed';
            const closedLabelLong = isImportedEvent ? 'Scheduled' : 'Registration Closed';

            if (showMemberEventTag) {
                return <div className={`tag tag-gray tag-xs ${blockClass}`} data-testid={statusTestId}>Member Event</div>;
            }

            switch (eventState?.value) {
                case 'pre_registration':
                    return (
                        <div className={`tag tag-gray tag-xs ${blockClass}`} data-testid={statusTestId}>
                            <span className={`d-block d-lg-none`} data-testid={shortTestId}>Pre Reg</span>
                            <span className={`d-none d-lg-block`} data-testid={longTestId}>Pre Registration</span>
                        </div>
                    );
                case 'open_registration':
                    return (
                        <div className={`tag tag-outline tag-xs ${blockClass}`} data-testid={statusTestId}>
                            <span className={`d-block d-lg-none`} data-testid={shortTestId}>Reg Open</span>
                            <span className={`d-none d-lg-block`} data-testid={longTestId}>Registration Open</span>
                        </div>
                    );
                case 'registration_closed':
                    return (
                        <div className={`tag tag-gray tag-xs ${blockClass}`} data-testid={statusTestId}>
                            <span className={`d-block d-lg-none`} data-testid={shortTestId}>{closedLabel}</span>
                            <span className={`d-none d-lg-block`} data-testid={longTestId}>{closedLabelLong}</span>
                        </div>
                    );
                case 'in_progress':
                    return <div className={`tag tag-xs ${blockClass}`} data-testid={statusTestId}>In Progress</div>;
                case 'completed':
                    return <div className={`tag tag-gray tag-xs ${blockClass}`} data-testid={statusTestId}>Completed</div>;
                default:
                    return <div />;
            }
        };

        const getTag = (block, tagPrefix, skipRoleTags = false) => {
            if (!published) {
                return <div className={`tag tag-gray tag-xs ${block ? 'tag-block' : ''}`} data-testid={tagPrefix && `${tagPrefix}.status-tag`}>Draft</div>;
            }
            if (skipRoleTags) {
                return getStatusTag(block, tagPrefix);
            }
            if (isPlaying && isOrganizer) {
                return (
                    <>
                        <div className={`tag tag-${colorStateV2()} tag-xs mr8`} data-testid={tagPrefix && `${tagPrefix}.player-tag`}>Player</div>
                        <div className={`tag tag-${colorStateV2()} tag-xs`} data-testid={tagPrefix && `${tagPrefix}.provider-tag`}>Provider</div>
                    </>
                );
            }
            if (isPlaying) {
                return <div className={`tag tag-${colorStateV2()} tag-xs ${block ? 'tag-block' : ''}`} data-testid={tagPrefix && `${tagPrefix}.player-tag`}>Player</div>;
            }
            if (isOrganizer) {
                return <div className={`tag tag-${colorStateV2()} tag-xs ${block ? 'tag-block' : ''}`} data-testid={tagPrefix && `${tagPrefix}.provider-tag`}>Provider</div>;
            }

            return getStatusTag(block, tagPrefix);
        };

        const descriptionRight = [];
        if (eventType?.label) {
            descriptionRight.push(eventType.label);
        }
        const tourName = tourTag || (eventTours?.length && eventTours[0].name);
        if (tourName) {
            descriptionRight.push(tourName);
        }

        const clubDetails = shouldHideClubName ? [] : [club?.name];
        clubDetails.push(locationCityStateZip);

        const descriptionMobileLine1V2 = [];
        const descriptionMobileLine2V2 = [];

        /* Description items mobile */
        if (hasPrizeMoney && prizeMoneyText && !isPaidHitEvent) {
            descriptionMobileLine1V2.push(prizeMoneyText);
        }

        if ((isOrganizer || event?.playerCountVisibility) && registeredCount > 0) {
            descriptionMobileLine2V2.push(`${registeredCount} Players`);
        }
        if (utrRange) {
            if (isPickleBallEvent) {
                descriptionMobileLine2V2.push(`${ratingTypes.pickleball} ${utrRange}`);
            } else {
                descriptionMobileLine2V2.push(`${ratingTypes.tennis} ${utrRange}`);
            }
        }
        if (priceRangeShort) {
            descriptionMobileLine1V2.push(`${priceRangeShort}`);
        }
        if (gender && !isPaidHitEvent) {
            descriptionMobileLine2V2.push(gender);
        }
        if (teamType && !isPaidHitEvent) {
            const gameType = getGameType();
            descriptionMobileLine2V2.push(gameType);
        }

        let eventDateString;
        if (eventSchedule?.eventDatesString) {
            eventDateString = eventSchedule.eventDatesString;
        } else if (eventSchedule) {
            eventDateString = getEventDateStringV2(eventSchedule);
        }

        if (unifiedProfileCard) {
            const model = buildUnifiedEventCardModel(event, {
                isPlaying,
                isOrganizer,
                isPaidHitEvent,
                shouldHideClubName,
                locationCityStateZip,
                isPickleBallEvent,
                eventDateString,
                compactCard: compactProfileCard,
                gameType: (teamType || eventSettings?.gameTypeId) && !isPaidHitEvent ? getGameType() : null,
            });
            const isNativeEvent = !isImportedProfileEvent(event) && !isLeagueSessionProfileEvent(event);

            return this.renderUnifiedCard({
                model,
                verifiedClassName,
                preserved: {
                    statusTag: isNativeEvent ? getTag(false, dataTestId && `${dataTestId}.unified`, true) : null,
                    recurringTag: (isNativeEvent && isRecurringEvent && isOrganizer) ? (
                        <div className={`tag tag-xs`} data-testid={dataTestId && `${dataTestId}.unified.recurring-tag`}>
                            <Img src="/img/v4/icon-recurring.svg" />
                            Recurring Event
                        </div>
                    ) : null,
                    paidHitterExperience: (isNativeEvent && isPaidHitEvent) ? (
                        <Dotdotdot clamp={1}>
                            <div data-testid={dataTestId && `${dataTestId}.unified.paid-hitter-experience`}>
                                {getPaidHitterExperience(paidHits, playerExperienceTypesObject, coachExperienceTypesObject, userIsPowered, isOrganizer)}
                            </div>
                        </Dotdotdot>
                    ) : null,
                    completeRegistration: showCompleteRegistration ? (
                        <div className="mt8">
                            <button
                                type="button"
                                className="btn btn-sm btn-primary text-uppercase mb8"
                                data-testid={dataTestId && `${dataTestId}.unified.complete-registration-btn`}
                            >
                                Complete Registration
                            </button>
                        </div>
                    ) : null,
                    usapLogo: (isNativeEvent && isPickleBallEvent) ? (
                        <USAPLogo
                            usapApplicationStatusId={usapApplicationStatusId}
                            usapVerificationTypeId={usapVerificationTypeId}
                            data-testid={dataTestId && `${dataTestId}.unified.usap-logo`}
                        />
                    ) : null,
                },
            });
        }

        if (isImportedProfileEvent(event)) {
            return this.renderImportedCard({ eventDateString, verifiedClassName });
        }

        return (
            <div style={showMemberEventTag ? { pointerEvents: 'none' } : {}}>
                <LinkAnchor
                    link={getEventRedirectUrl(id, source)}
                    useAnchor={getUseAnchorForEventLink(source)}
                    target={'_self'}
                    data-testid={dataTestId && `${dataTestId}.card-link`}
                >
                    <div
                        // eslint-disable-next-line max-len
                        className={`utr-card event-card  ${force?.large ? 'event-card-large' : ''} ${force?.featured ? 'event-card-featured' : ''}`}
                        style={{ minHeight: (usapApplicationStatusId || usapVerificationTypeId) ? cardHeight : '', height: (usapApplicationStatusId || usapVerificationTypeId) ? 'fit-content' : '' }}
                    >
                        <SportLabel sportTypeId={sportTypeId} data-testid={dataTestId && `${dataTestId}.sport-label`} />
                        <div className={` list-view  ${forceCardView || force.cardView ? 'd-none' : 'd-none d-md-block'} bb0`}>
                            <div className="row">
                                <div className="col-md-6 content-wrapper">
                                    <div className="club-and-name">
                                        <div className="club-name show-ellipsis" data-testid={dataTestId && `${dataTestId}.desktop.club-name`}>{!isPaidHitEvent && club?.name}</div>
                                        <div className={`name ${utrType?.value === 'sanctioned' ? 'hasSanctioned' : ''}`} title={name} data-testid={dataTestId && `${dataTestId}.desktop.name`}>
                                            <Dotdotdot clamp={1}>
                                                {isDemoEvent ? '[DEMO] ' : ''}{this.formatEventName(name)}
                                            </Dotdotdot>
                                        </div>
                                    </div>

                                    <div className="description">
                                        {isPaidHitEvent && (
                                            <div className={'text-black'} data-testid={dataTestId && `${dataTestId}.desktop.paid-hitter-experience`}>
                                                {getPaidHitterExperience(paidHits, playerExperienceTypesObject, coachExperienceTypesObject, userIsPowered, isOrganizer)}
                                            </div>
                                        )}
                                        <div data-testid={dataTestId && `${dataTestId}.desktop.description-line1`}>
                                            <Description desc={descriptionLine1} />
                                        </div>
                                        <div data-testid={dataTestId && `${dataTestId}.desktop.description-line2`}>
                                            <Description desc={descriptionLine2} />
                                        </div>
                                    </div>
                                    <div>
                                        {isPickleBallEvent && <USAPLogo usapApplicationStatusId={usapApplicationStatusId} usapVerificationTypeId={usapVerificationTypeId} data-testid={dataTestId && `${dataTestId}.desktop.usap-logo`} />}
                                    </div>

                                </div>
                                <div className="col-md-6 content-wrapper aie">
                                    <div className="date-wrapper">
                                        <div className={`d-flex jce aic mb8`}>
                                            <div className={`tags`}>
                                                {getTag(false, dataTestId && `${dataTestId}.desktop`)}
                                            </div>
                                            {((utrType?.value === 'sanctioned') || hasPrizeMoney) && (
                                                <div className={`badge-wrapper text-teal ml4`} data-testid={dataTestId && `${dataTestId}.desktop.badge-wrapper`}>
                                                    {(utrType?.value === 'sanctioned') && (
                                                        <div className={`utr-icon ${verifiedClassName} ml12`} title="Sanctioned" data-testid={dataTestId && `${dataTestId}.desktop.sanctioned-badge`} />
                                                    )}
                                                    {hasPrizeMoney &&
                                                        <i className="prize-event-icon fas fa-usd-circle ml12" title="Has Prize Money" data-testid={dataTestId && `${dataTestId}.desktop.prize-money-badge`} />}
                                                </div>
                                            )}
                                        </div>
                                        <div className="date" data-testid={dataTestId && `${dataTestId}.desktop.date`}>
                                            {eventDateString}
                                        </div>
                                    </div>
                                    {selectButton ? (
                                        <button
                                            type="button"
                                            className={`btn btn-primary btn-sm`}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                e.preventDefault();
                                                onSelectButtonClick(id);
                                            }}
                                            data-testid={dataTestId && `${dataTestId}.desktop.select-btn`}
                                        >
                                            Select
                                        </button>
                                    ) : (
                                        <div className="description-right show-ellipsis" data-testid={dataTestId && `${dataTestId}.desktop.description-right`}><Description desc={descriptionRight} /></div>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div
                            // eslint-disable-next-line max-len
                            className={`card-view ${forceCardView || force.cardView ? 'd-block' : 'd-block d-md-none'} ${force?.small ? 'card-small' : ''} ${(showInvitation || showRegister || showCompleteRegistration) && 'invitation'}`}
                            style={{ minHeight: ((usapApplicationStatusId || usapVerificationTypeId) && usapApplicationStatusId !== TmsEventUsapApplicationStatusTypes.DECLINED) ? cardHeight : '', height: ((usapApplicationStatusId || usapVerificationTypeId) && usapApplicationStatusId !== TmsEventUsapApplicationStatusTypes.DECLINED) ? 'fit-content' : '' }}
                        >
                            <div className="content-wrapper">
                                <div className="club-and-name">
                                    <div className="date">
                                        <span data-testid={dataTestId && `${dataTestId}.mobile.date`}>{eventDateString}</span>
                                        <div className={`tags`}>
                                            {isRecurringEvent && isOrganizer && (
                                                <div className={`tag tag-xs`} data-testid={dataTestId && `${dataTestId}.mobile.recurring-tag`}>
                                                    <Img src="/img/v4/icon-recurring.svg" />
                                                    Recurring Event
                                                </div>
                                            )}
                                            {getTag(false, dataTestId && `${dataTestId}.mobile`)}
                                        </div>
                                    </div>
                                    <div className={`name d-flex break-word ${utrType?.value === 'sanctioned' ? 'hasSanctioned' : ''}`} title={name} data-testid={dataTestId && `${dataTestId}.mobile.name`}>
                                        <Dotdotdot clamp={isPaidHitEvent ? 1 : 2}>
                                            {this.formatEventName(name)}
                                        </Dotdotdot>
                                        <div className={`badge-wrapper`} data-testid={dataTestId && `${dataTestId}.mobile.badge-wrapper`}>
                                            {(utrType?.value === 'sanctioned') &&
                                                <div className={`utr-icon ${verifiedClassName}`} title="Sanctioned" data-testid={dataTestId && `${dataTestId}.mobile.sanctioned-badge`} />}
                                        </div>
                                    </div>
                                    {isPaidHitEvent && (
                                        <Dotdotdot clamp={1}>
                                            <div data-testid={dataTestId && `${dataTestId}.mobile.paid-hitter-experience`}>
                                                {getPaidHitterExperience(paidHits, playerExperienceTypesObject, coachExperienceTypesObject, userIsPowered, isOrganizer)}
                                            </div>
                                        </Dotdotdot>
                                    )}
                                    <div className="club-name show-ellipsis" data-testid={dataTestId && `${dataTestId}.mobile.club-name`}><Description desc={clubDetails} /></div>
                                </div>
                                <div className="description">
                                    {!showInvitation && (
                                        <>
                                            <div data-testid={dataTestId && `${dataTestId}.mobile.description-line1`}>
                                                <Description desc={descriptionMobileLine1V2} />
                                            </div>
                                            <div data-testid={dataTestId && `${dataTestId}.mobile.description-line2`}>
                                                <Description desc={descriptionMobileLine2V2} />
                                                {selectButton && (
                                                    <div className="select">
                                                        <button
                                                            type="button"
                                                            className={`btn btn-primary btn-sm`}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                e.preventDefault();
                                                                onSelectButtonClick(id);
                                                            }}
                                                            data-testid={dataTestId && `${dataTestId}.mobile.select-btn`}
                                                        >
                                                            Select
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        </>
                                    )}
                                    {showInvitation && invitingMember && (
                                        <div className="text-grey d-flex aic mb24" data-testid={dataTestId && `${dataTestId}.mobile.invitation-text`}>
                                            <div className="utr-icon icon-profile mr8" />
                                            {invitingMember.firstName} {invitingMember.lastName[0]}. (<Utr player={invitingMember} force={{ ratingType: 'singles', showDecimals: true }} sportTypeId={sportTypeId} data-testid={dataTestId && `${dataTestId}.mobile.invitation-utr`} />) invited you to play
                                        </div>
                                    )}
                                    {showInvitation && isFreePlayEvent && (
                                        <div className="d-flex mb16">
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-default text-uppercase ml-auto mr16 mb8"
                                                onClick={onDeclineClick}
                                                data-testid={dataTestId && `${dataTestId}.mobile.decline-btn`}
                                            >
                                                {'I\'ll Pass'}
                                            </button>
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-primary text-uppercase mb8"
                                                onClick={onAcceptClick}
                                                data-testid={dataTestId && `${dataTestId}.mobile.accept-btn`}
                                            >
                                                {'I\'ll Play'}
                                            </button>
                                        </div>
                                    )}
                                    {showRegister && (
                                        <div className="col-12 text-center mb16">
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-primary text-uppercase mb8"
                                                data-testid={dataTestId && `${dataTestId}.mobile.register-btn`}
                                            >
                                                Register
                                            </button>
                                        </div>
                                    )}
                                </div>
                                {showCompleteRegistration && (
                                    <div className="mt8">
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-primary text-uppercase mb8"
                                            data-testid={dataTestId && `${dataTestId}.mobile.complete-registration-btn`}
                                        >
                                            Complete Registration
                                        </button>
                                    </div>
                                )}
                                {isPickleBallEvent && <USAPLogo usapApplicationStatusId={usapApplicationStatusId} usapVerificationTypeId={usapVerificationTypeId} data-testid={dataTestId && `${dataTestId}.mobile.usap-logo`} />}
                            </div>
                        </div>
                    </div>
                </LinkAnchor>
                {isOnDashboard && !showCompleteRegistration && <AddEventToCalendar isOnDashboard={isOnDashboard} uiElement={'link'} eventDetails={event} loggedInUserId={auth?.user?.id} data-testid={dataTestId && `${dataTestId}.add-to-calendar`} />}
            </div>
        );
    }
}

function mapStateToProps({ coreUI }) {
    return { coreUI };
}

EventCard.propTypes = {
    coreUI: PropTypes.shape().isRequired,
    event: PropTypes.shape().isRequired,
    showCompleteRegistration: PropTypes.bool,
    auth: PropTypes.shape({}).isRequired,
    isOnDashboard: PropTypes.bool,
    showRegister: PropTypes.bool,
    onDeclineClick: PropTypes.func,
    onAcceptClick: PropTypes.func,
    showInvitation: PropTypes.bool,
    onSelectButtonClick: PropTypes.func,
    isAdminOrOwner: PropTypes.bool,
    tourTag: PropTypes.shape({}),
    force: PropTypes.shape({}),
    forceCardView: PropTypes.shape({}),
    isProfilePage: PropTypes.bool,
    unifiedProfileCard: PropTypes.bool,
    showShareAction: PropTypes.bool,
    compactProfileCard: PropTypes.bool,
    entryPoint: PropTypes.string,
    dispatch: PropTypes.func,
    history: PropTypes.shape({ push: PropTypes.func }),
    'data-testid': PropTypes.string,
};

EventCard.defaultProps = {
    showCompleteRegistration: false,
    isOnDashboard: false,
    showRegister: false,
    onDeclineClick: () => { },
    onAcceptClick: () => { },
    showInvitation: false,
    onSelectButtonClick: () => { },
    isAdminOrOwner: false,
    tourTag: null,
    force: {},
    forceCardView: null,
    isProfilePage: false,
    unifiedProfileCard: false,
    showShareAction: false,
    compactProfileCard: false,
    entryPoint: null,
    dispatch: null,
    history: null,
    'data-testid': null,
};

export default connect(mapStateToProps)(EventCard);
